import { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";

import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import trade_favicon_transparent_bg from "../../../../assets/images/trade_favicon_transparent_bg.png";
import trade_text_transparent from "../../../../assets/images/trade_text_transparent.png";

import {
  faBell,
  faHeart,
  faMessage,
} from "@fortawesome/free-regular-svg-icons";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { logoutUser, setCurrentUser } from "actions/authAction";
import { setCurrentFavorites } from "actions/favListAction";
import { setCurrentNotifications } from "actions/notificationAction";
import axiosClient from "api";
import {
  countUnread,
  mergeNotification,
  normalizeNotifications,
} from "util/notifications";
import { getFileLink } from "util/s3";
import { toastify, toastType } from "util/toastify";
import store from "../../../../store/store";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "1px solid currentColor",
      content: '""',
    },
  },
}));

// Safety net for the bell: a socket push is the instant path, but if one is
// ever lost (reconnect, server restart) this keeps the badge right without a
// page refresh.
const NOTIFICATION_POLL_INTERVAL = 20000;
// The server counts a member as "Active now" for 5 minutes after it last
// heard from them; two minutes keeps that green without a chatty socket.
const PRESENCE_HEARTBEAT_INTERVAL = 2 * 60 * 1000;

const Header = ({
  auth,
  favourite,
  notification,
  socket,
  setCurrentUser,
  setCurrentNotifications,
  setCurrentFavorites,
}) => {

  const [showState, setShowState] = useState(true);
  const [avatarLink, setAvatarLink] = useState("");
  const [pushNotification, setPushNotification] = useState([]);
  const [todays, setTodays] = useState([]);
  const [thisMonth, setThisMonth] = useState([]);
  const [inThePast, setInThePast] = useState([]);
  const [numberOfNotifications, setNumberOfNotifications] = useState(0);
  const [userFlag, setUserFlag] = useState(false);
  const [socketFlag, setSocketFlag] = useState(true);

  const location = useLocation();
  const history = useHistory();

  const headerSearch = useRef(null);
  // Latest notification list, readable from socket handlers without a stale
  // closure (the Redux-connected prop lags one render behind).
  const notificationsRef = useRef([]);
  const refreshInFlight = useRef(false);
  const locationRef = useRef(location);

  function convertInput(input) {
    if (Array.isArray(input)) {
      return input;
    }
    if (typeof input === "string") {
      try {
        const parsed = JSON.parse(input);
        if (Array.isArray(parsed)) {
          return parsed;
        } else {
          return [];
        }
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  useEffect(() => {
    const next = notification.notifications || [];
    notificationsRef.current = next;
    setPushNotification((prev) =>
      prev.length === 0 && next.length === 0 ? prev : next
    );
    setNumberOfNotifications(countUnread(next));
  }, [notification]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // useEffect(() => {
  //   if (!socket || !auth.user?.userId) return;

  //   const handleConnect = () => {
  //     console.log("✅ Socket connected");
  //     socket.emit("NewUser", { userId: auth.user.userId });
  //   };

  //   const handleReconnect = () => {
  //     console.log("🔁 Socket reconnected");
  //     socket.emit("Reconnection", { userId: auth.user.userId });
  //   };

  //   socket.on("connect", handleConnect);
  //   socket.io.on("reconnect", handleReconnect);

  //   return () => {
  //     socket.off("connect", handleConnect);
  //     socket.io.off("reconnect", handleReconnect);
  //   };
  // }, [auth?.user?.userId, socket]);


  useEffect(() => {
    if (auth.user && auth.user.userId && !userFlag)
      axiosClient
        .get(`/api/users/${auth.user.userId}`)
        .then((res) => {
          setUserFlag(true);
          setCurrentUser(res.data.data.user);
          setCurrentFavorites(convertInput(res.data?.data?.user?.favList));
        })
        .catch((error) => console.error(error));
  }, [
    auth.user,
    auth.user.userId,
    setCurrentUser,
    setCurrentFavorites,
    userFlag,
  ]);

  useEffect(() => {
    if (
      location.pathname === "/login" ||
      location.pathname === "/register" ||
      location.pathname === "/service"
    ) {
      setShowState(false);
    } else {
      setShowState(true);
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);



  const publishNotifications = useCallback(
    (next) => {
      notificationsRef.current = next;
      setCurrentNotifications(next);
    },
    [setCurrentNotifications]
  );

  // Pull the unread list from the server. Runs on mount, on (re)connect, when
  // the tab regains focus and on a timer, so the bell stays right even when a
  // socket push never arrives.
  const refreshNotifications = useCallback(async () => {
    const userId = auth.user?.userId;
    if (!auth.isAuthenticated || !userId || refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      // window.location, not the router's: the Messages page switches rooms
      // with history.replaceState so the router (and locationRef) lag behind.
      const { pathname, search } = window.location;
      const payload = { id: userId };
      if (
        pathname === "/chat-history" ||
        pathname === "/ask-question" ||
        pathname === "/task"
      ) {
        const roomId = new URLSearchParams(search).get("roomId");
        if (roomId) payload.roomId = roomId;
      }
      const res = await axiosClient.post(
        "/api/notification/get-unread-only",
        payload
      );
      publishNotifications(
        await normalizeNotifications(res.data.data.notifications.rows, userId)
      );
      // The same response carries the server's view of the points balance.
      // Points move on completion/cancellation, and the live socket push for
      // that can be missed (other server instance, tab closed at the time);
      // this keeps the header right within one poll either way.
      const serverBalance = res.data.data.balance;
      if (typeof serverBalance === "number") {
        const current = store.getState().auth.user;
        if (current && current.userId === userId && current.balance !== serverBalance) {
          setCurrentUser({ ...current, balance: serverBalance });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      refreshInFlight.current = false;
    }
  }, [auth.isAuthenticated, auth.user?.userId, publishNotifications, setCurrentUser]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.userId) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshNotifications();
    };
    const interval = setInterval(
      refreshNotifications,
      NOTIFICATION_POLL_INTERVAL
    );
    window.addEventListener("focus", refreshNotifications);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", refreshNotifications);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [auth.isAuthenticated, auth.user?.userId, refreshNotifications]);

  useEffect(() => {
    let mounted = true; // Flag to track if component is mounted

    if (auth.user.avatar) {
      let promise = getFileLink(auth.user.avatar)

      promise.then(
        (url) => {
          // Check if component is still mounted before updating state
          if (mounted) {
            setTimeout(() => {
              setAvatarLink(url);
            }, 3000);
          }
        },
        (err) => {
          console.error(err);
        }
      );
    }

    // Cleanup function to cancel async task if component unmounts
    return () => {
      mounted = false;
    };
  }, [auth.user.avatar]);

  useEffect(() => {
    if (!socket || !auth.user?.userId) return;

    const handleConnect = () => {
      console.log("✅ Socket connected");
      socket.emit("NewUser", { userId: auth.user.userId });
      setSocketFlag(false);
    };

    const handleReconnect = () => {
      console.log("🔁 Socket reconnected");
      socket.emit("Reconnection", { userId: auth.user.userId });
    };

    if (socket.connected) {
      handleConnect();
    } else {
      socket.on("connect", handleConnect);
    }

    socket.io.on("reconnect", handleReconnect);

    // "Active now" is driven by the last time the server heard from us. Ping
    // while the tab is actually in front of the user, not from a background
    // tab left open all day.
    const beat = () => {
      if (socket.connected && document.visibilityState === "visible") {
        socket.emit("Heartbeat", { userId: auth.user.userId });
      }
    };
    const heartbeat = setInterval(beat, PRESENCE_HEARTBEAT_INTERVAL);
    document.addEventListener("visibilitychange", beat);

    return () => {
      socket.off("connect", handleConnect);
      socket.io.off("reconnect", handleReconnect);
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [auth.user?.userId, socket]);

  useEffect(() => {
    const userId = auth.user?.userId;
    if (!socket || !auth.isAuthenticated || !userId) return undefined;

    const onNewNotification = async (data) => {
      if (!data || data.senderId === userId) return;
      const [item] = await normalizeNotifications([data], userId);
      if (!item) return;
      publishNotifications(mergeNotification(notificationsRef.current, item));
    };
    // Pushes sent while we were disconnected are gone; resync after a
    // (re)connect so the badge catches up without a page refresh.
    const onConnect = () => refreshNotifications();

    socket.on("newNotification", onNewNotification);
    socket.on("connect", onConnect);
    return () => {
      socket.off("newNotification", onNewNotification);
      socket.off("connect", onConnect);
    };
  }, [
    socket,
    auth.isAuthenticated,
    auth.user?.userId,
    publishNotifications,
    refreshNotifications,
  ]);

  useEffect(() => {
    if (!socket) return undefined;
    const onCancelMilestone = (data) => {
      const current = store.getState().auth.user;
      setCurrentUser({
        ...current,
        balance: (current.balance || 0) + data.amount,
      });
    };
    socket.on("cancelMilestone", onCancelMilestone);
    return () => socket.off("cancelMilestone", onCancelMilestone);
  }, [socket, setCurrentUser]);

  useEffect(() => {
    const tmp1 = [];
    const tmp2 = [];
    const tmp3 = [];
    const today = new Date();
    pushNotification.forEach((item) => {
      const date = new Date(item.createdAt);
      if (
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      ) {
        if (date.getDate() === today.getDate()) {
          tmp1.push(item);
        } else {
          tmp2.push(item);
        }
      } else {
        tmp3.push(item);
      }
    });
    setTodays(tmp1);
    setThisMonth(tmp2);
    setInThePast(tmp3);
  }, [pushNotification]);

  const applyNotificationRows = async (rows) =>
    publishNotifications(await normalizeNotifications(rows, auth.user.userId));

  // Flip one notification to READ locally so the badge reacts on click, before
  // the server round-trip confirms it.
  const markReadLocally = (id) =>
    publishNotifications(
      notificationsRef.current.map((n) =>
        n.id === id ? { ...n, status: "READ" } : n
      )
    );

  // Open the task chat behind a notification. The server attaches `taskId` to
  // every notification it returns, so this is normally a straight navigation;
  // the lookup is only a fallback for a socket push, which doesn't carry it.
  const openTaskFromNotification = (item) => {
    if (item.taskId) {
      history.push(`/task?taskId=${item.taskId}`);
      return;
    }
    axiosClient
      .post("/api/task/get-task-id", {
        roomId: item.roomId,
        roomType: item.roomType,
      })
      .then((res) => history.push(`/task?taskId=${res.data.data.id}`))
      .catch((error) => console.error(error));
  };

  const markTaskNotificationSeen = (item) =>
    axiosClient
      .post("/api/notification/mark-task-notification-seen", { id: item.id })
      .then((res) => applyNotificationRows(res.data.data.notifications.rows))
      .catch((err) => console.error(err));

  // Navigate first and mark the notification read in the background: the
  // header stays mounted across routes so the list still updates, and the user
  // isn't left looking at the dropdown while requests round-trip.
  const checkNotification = (item) => {
    markReadLocally(item.id);
    switch (item.type) {
      case "NEWMESSAGE":
        if (item.roomType === "AQ") {
          // Same screen the envelope icon opens, with this conversation
          // selected — one chat UI whichever way you get there.
          history.push(`/chat-history?roomId=${item.roomId}`);
        } else if (item.roomType === "TD") {
          openTaskFromNotification(item);
        }
        return axiosClient
          .post("/api/notification/mark-messages-seen", {
            type: item.type,
            roomId: item.roomId,
            receiverId: auth.user.userId,
          })
          .then((res) => applyNotificationRows(res.data.data.notifications.rows))
          .catch((err) => console.error(err));
      case "SERVICEREQUESTED":
      case "SERVICEREJECTED":
      case "SERVICECANCELLED":
        history.push(`/dashboard?type=${item.type}`);
        return markTaskNotificationSeen(item);
      // A completed service goes straight to the task page, where the rating
      // and review box is, instead of bouncing through the dashboard.
      case "SERVICECOMPLETED":
      case "ANNOUNCEMENT":
      case "SERVICEACCEPTED":
      case "SERVICECANCELREQUEST":
      case "SERVICECANCELREJECTED":
      case "SERVICECANCELACCEPTED":
      case "SERVICECOMPLETEREQUEST":
      case "SERVICECOMPLETEREJECTED":
      case "SERVICECOMPLETEACCEPTED":
      case "SERVICEREVIEWED":
        if (item.roomType === "TD") openTaskFromNotification(item);
        return markTaskNotificationSeen(item);
      default:
        return null;
    }
  };

  const logoutHandler = () => {
    store.dispatch(logoutUser());
    history.push("/");
  };

  const markAllRead = () => {
    // Clear the badge right away, then reconcile with what the server returns.
    publishNotifications(
      notificationsRef.current.map((n) =>
        n.status === "UNREAD" ? { ...n, status: "READ" } : n
      )
    );
    axiosClient
      .post("/api/notification/mark-all-read", {
        receiverId: auth.user.userId,
      })
      .then((res) => applyNotificationRows(res.data.data.notifications.rows))
      .catch((err) => {
        console.error(err);
        toastify(
          "Could not mark notifications as read. Please try again.",
          toastType.error
        );
        refreshNotifications();
      });
  };

  const adminToken = localStorage.getItem("adminToken");

  return (
    <header className="flex items-center py-10 min-h-[140px]">
      {auth.isAuthenticated ? (
        <div className="container justify-between max-md:block">
          <div className="relative header-left flex items-center max-md:mb-6 xl:mr-16">
            {/* <Link to="/">
              <>
                <span className="text-3xl font-semibold tracking-wide text-[#1cc400]">
                  TRADE
                </span>
                <span className="text-3xl font-semibold tracking-wide text-black">
                  IT
                </span>
                <span className="text-3xl font-semibold tracking-wide text-[#1cc400]">
                  FWD
                </span>
                <sup className="absolute top-0 text-xs font-semibold text-[#1cc400]">
                  TM
                </sup>
              </>
            </Link> */}
            <Link to="/">
              <img src={trade_text_transparent} alt='Header Logo' style={{ maxWidth: 300, width: '100%' }} />
            </Link>
          </div>
          {showState && (
            <div className="header-right flex items-center flex-1">
              {location.pathname !== "/single-service" ? (
                <>
                  <div className="lg:ml-auto" />
                  <Link
                    to="/dashboard"
                    className="text-[#1cc400] text-xl ml-6 max-lg:hidden"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="text-[#1cc400] text-xl ml-6 max-lg:hidden"
                  >
                    How it Works
                  </Link>
                  <Link
                    to="/help"
                    className="text-[#1cc400] text-xl ml-6 xl:mr-8 max-lg:hidden"
                  >
                    Help
                  </Link>
                  <form className="relative flex flex-1 max-xl:hidden max-w-[500px]">
                    <input
                      ref={headerSearch}
                      className="flex flex-1 p-3 border-2 border-solid border-zinc-300 rounded-2xl focus-visible:border-zinc-300 outline-zinc-300 text-black max-sm:min-w-[260px]"
                      type="text"
                      name="search"
                      placeholder="Search a service/member or leave empty to see all"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          headerSearch.current.blur();
                          localStorage.setItem(
                            "searchKeyword",
                            headerSearch.current.value
                          );
                          history.push("/single-service");
                        }
                      }}
                      required
                    ></input>
                    <button
                      onClick={() => {
                        localStorage.setItem(
                          "searchKeyword",
                          headerSearch.current.value
                        );
                        history.push("/single-service");
                      }}
                      className="btn btn-search absolute top-0.5 right-0.5 inline-block py-2.5 px-[30px] bg-second rounded-r-[14px] rounded-l-none transition-color duration-300 outline-none border-none hover:bg-main"
                      type="button"
                    >
                      <FontAwesomeIcon
                        icon={faSearch}
                        style={{ fontSize: 20, color: "white" }}
                      />
                    </button>
                  </form>
                </>
              ) : (
                <div className="flex-1" />
              )}
              {/* On small screens the header search field is hidden, so this
                  icon is the way into the member list. It goes straight there
                  with no keyword (which lists every member) rather than
                  opening a second search bar over the one already on screen. */}
              <div className="relative xl:hidden flex z-1 transition-all duration-300 cursor-pointer max-lg:!ml-auto max-2xl:ml-0 ml-0">
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Browse members"
                  onClick={() => {
                    localStorage.setItem("searchKeyword", "");
                    history.push("/single-service");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      localStorage.setItem("searchKeyword", "");
                      history.push("/single-service");
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ fontSize: 23, color: "#000" }}
                    className="ml-[41px] max-md:ml-0"
                  />
                </div>
              </div>
              <div className="group/first ml-[27px] flex relative z-1 transition-all duration-300 cursor-pointer">
                <Link to="/chat-history">
                  <FontAwesomeIcon
                    icon={faMessage}
                    style={{ fontSize: 23, color: "#000" }}
                    className=""
                  />
                </Link>
              </div>
              <div className="group/first ml-[27px] flex relative z-1 transition-all duration-300 cursor-pointer">
                <FontAwesomeIcon
                  icon={faBell}
                  style={{ fontSize: 23, color: "#000" }}
                  className=""
                />
                <span className="absolute -right-2 -top-2 flex items-center justify-center rounded-full px-1 text-xs text-[#fff] bg-main w-5 h-5">
                  {numberOfNotifications}
                </span>
                <div className="absolute opacity-0 invisible top-full pt-4 sm:-right-6 right-[-216px] bg-transparent group-hover/first:opacity-100 transition-all duration-0 group-hover/first:duration-300 border-none cursor-pointer w-full group-hover/first:z-20 group-hover/first:!visible min-w-[360px]">
                  <div className="px-6 py-6 bg-white shadow-md rounded-[20px]">
                    <div className="scroll-container relative max-h-80 overflow-y-scroll">
                      {todays.length > 0 && <h3 className="">Today</h3>}
                      {todays.map((item, index) => (
                        <>
                          {item.sender && (
                            <div
                              onClick={() => checkNotification(item)}
                              key={`notification-today-${index}`}
                              className="flex items-center border-b border-solid border-t-0 border-x-0 border-gray-200"
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                className="justify-center mt-6 mb-3"
                              >
                                <StyledBadge
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  color={
                                    (new Date() -
                                      new Date(item.sender.updatedAt)) /
                                      (1000 * 60) <
                                      5
                                      ? "primary"
                                      : "warning"
                                  }
                                  variant="dot"
                                >
                                  <Avatar
                                    alt={`${item.sender.firstName} ${item.sender.lastName}`}
                                    src={`${item.sender.media
                                      ? item.sender.media.url
                                      : (trade_favicon_transparent_bg || "")
                                      }`}
                                  />
                                </StyledBadge>
                              </Stack>
                              <div className="ml-3 mt-[5px] text-start">
                                <div className="flex items-end">
                                  <p className="mr-2 font-bold text-[14px]">
                                    {`${item.sender.firstName} ${item.sender.lastName}`}
                                  </p>
                                  <p className="text-[13px]">{item.content}</p>
                                </div>
                                <span className="text-[12px] text-[#A0A1AB]">
                                  {item.elapsedTime}
                                </span>
                                {item.status === "UNREAD" && (
                                  <span className="text-white bg-red-500 text-xs px-1 py-0.5 rounded ml-2">
                                    UNREAD
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ))}
                      {thisMonth.length > 0 && (
                        <h3 className="mt-4">This Month</h3>
                      )}
                      {thisMonth.map((item, index) => (
                        <>
                          {item.sender && (
                            <div
                              onClick={() => checkNotification(item)}
                              key={`notification-this-month-${index}`}
                              className="flex items-center border-b border-solid border-t-0 border-x-0 border-gray-200"
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                className="justify-center mt-6 mb-3"
                              >
                                <StyledBadge
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  variant="dot"
                                  color={
                                    (new Date() -
                                      new Date(item.sender.updatedAt)) /
                                      (1000 * 60) <
                                      5
                                      ? "primary"
                                      : "warning"
                                  }
                                >
                                  <Avatar
                                    alt={`${item.sender.firstName} ${item.sender.lastName}`}
                                    src={`${item.sender.media
                                      ? item.sender.media.url
                                      : (trade_favicon_transparent_bg || "")
                                      }`}
                                  />
                                </StyledBadge>
                              </Stack>
                              <div className="ml-3 mt-[5px] text-start">
                                <div className="flex items-end">
                                  <p className="mr-2 font-bold text-[14px]">
                                    {`${item.sender.firstName} ${item.sender.lastName}`}
                                  </p>
                                  <p className="text-[13px]">{item.content}</p>
                                </div>
                                <span className="text-[12px] text-[#A0A1AB]">
                                  {item.elapsedTime}
                                </span>
                                {item.status === "UNREAD" && (
                                  <span className="text-white bg-red-500 text-xs px-1 py-0.5 rounded ml-2">
                                    UNREAD
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ))}
                      {inThePast.length > 0 && <h3 className="mt-4">Others</h3>}
                      {inThePast.map((item, index) => (
                        <>
                          {item.sender && (
                            <div
                              onClick={() => checkNotification(item)}
                              key={`notification-this-month-${index}`}
                              className="flex items-center border-b border-solid border-t-0 border-x-0 border-gray-200"
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                className="justify-center mt-6 mb-3"
                              >
                                <StyledBadge
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  variant="dot"
                                  color={
                                    (new Date() -
                                      new Date(item.sender.updatedAt)) /
                                      (1000 * 60) <
                                      5
                                      ? "primary"
                                      : "warning"
                                  }
                                >
                                  <Avatar
                                    alt={`${item.sender.firstName} ${item.sender.lastName}`}
                                    src={`${item.sender.media
                                      ? item.sender.media.url
                                      : (trade_favicon_transparent_bg || "")
                                      }`}
                                  />
                                </StyledBadge>
                              </Stack>
                              <div className="ml-3 mt-[5px] text-start">
                                <div className="flex items-end">
                                  <p className="mr-2 font-bold text-[14px]">
                                    {`${item.sender.firstName} ${item.sender.lastName}`}
                                  </p>
                                  <p className="text-[13px]">{item.content}</p>
                                </div>
                                <span className="text-[12px] text-[#A0A1AB]">
                                  {item.elapsedTime}
                                </span>
                                {item.status === "UNREAD" && (
                                  <span className="text-white bg-red-500 text-xs px-1 py-0.5 rounded ml-2">
                                    UNREAD
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ))}
                      {pushNotification.length < 1 && (
                        <div className="flex items-center">
                          <div className="ml-3 mt-[5px] text-start">
                            <div className="flex items-end">
                              <p className="mr-2 font-bold text-[14px]">
                                No New Notification
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {pushNotification.length > 0 && (
                      // A full-width row rather than a bare word. The old
                      // button was only as big as its text (about 18px tall),
                      // so where the font renders a little differently (the
                      // client saw it on Chrome for Mac) the words sat just
                      // outside the part that took the click. The whole strip
                      // is clickable now, and it sits above the scrolling list
                      // so nothing can cover it.
                      <div className="relative z-10 mt-2 pt-2 border-t border-solid border-x-0 border-b-0 border-gray-200">
                        <button
                          type="button"
                          onClick={markAllRead}
                          className="block w-full py-2 font-bold text-[14px] text-center border-none bg-transparent rounded-[10px] cursor-pointer hover:bg-gray-100 hover:text-blue-700"
                        >
                          Mark All Read
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="group/first ml-[24px] flex relative z-1 transition-all duration-300 cursor-pointer">
                <Link to="/favourite-list">
                  <FontAwesomeIcon
                    icon={faHeart}
                    style={{ fontSize: 23, color: "#000" }}
                    className=""
                  />
                </Link>
              </div>
              <div className="group/first flex relative py-[8px] pl-[10px] pr-[30px] ml-[36px] mr-[-20px] rounded-[20px] bg-main z-1 transition-all duration-300 cursor-pointer">
                <img
                  src="/images/wallet.png"
                  alt="wallet"
                  width="26"
                  height="23"
                />
                <span className="ml-[3px] text-white">
                  {auth.user && auth.user.balance ? auth.user.balance : 0}
                </span>
                <ul className="absolute min-w-fit opacity-0 invisible top-12 left-0 right-0 pl-0 list-none group-hover/first:opacity-100 transition-all duration-300 bg-[#f5f5f5] border border-solid cursor-pointer !border-main w-full group-hover/first:!visible group-hover/first:z-20">
                  <li className="group/second px-2 py-2 border-solid border-0 transition-all duration-300 hover:bg-main">
                    <Link
                      to="/my-profile"
                      className="!text-main group-hover/second:!text-white"
                    >
                      Profile
                    </Link>
                  </li>
                  <li className="group/second lg:hidden px-2 py-2 border-solid border-0 transition-all duration-300 hover:bg-main">
                    <Link
                      to="/dashboard"
                      className="!text-main group-hover/second:!text-white whitespace-nowrap"
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li className="group/second lg:hidden px-2 py-2 border-solid border-0 transition-all duration-300 hover:bg-main">
                    <Link
                      to="/how-it-works"
                      className="!text-main group-hover/second:!text-white whitespace-nowrap"
                    >
                      How It Works
                    </Link>
                  </li>
                  <li className="group/second lg:hidden px-2 py-2 border-solid border-0 transition-all duration-300 hover:bg-main">
                    <Link
                      to="/help"
                      className="!text-main group-hover/second:!text-white"
                    >
                      Help
                    </Link>
                  </li>
                  <li className="group/second px-2 py-2 transiton-all duration-300 hover:bg-main">
                    <span
                      onClick={() => logoutHandler()}
                      className="!text-main group-hover/second:!text-white"
                    >
                      Logout
                    </span>
                  </li>
                </ul>
              </div>
              <Stack
                direction="row"
                spacing={2}
                className="justify-center z-10 bg-gray-200 rounded-full"
              >
                <Avatar
                  alt="user avatar"
                  src={avatarLink || trade_favicon_transparent_bg}
                  className="min-w-[62px] min-h-[62px]"
                />
              </Stack>
            </div>
          )}
        </div>
      ) : (
        <div className="container justify-between max-md:block">
          <div className="relative header-left flex items-center max-md:mb-6">
            <Link to="/">
              <img src={trade_text_transparent} alt='Header Logo' style={{ maxWidth: 300, width: '100%' }} />
            </Link>
          </div>
          {showState && (
            <div className="header-right flex items-center justify-end">
              {adminToken ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100">
                    <div className="w-7 h-7 rounded-full bg-[#1cc400] flex items-center justify-center text-white text-xs font-bold">
                      {(() => {
                        try {
                          const d = JSON.parse(atob(adminToken.split(".")[1]));
                          const name = (d.firstName || d.name || d.email || "A");
                          return name[0].toUpperCase();
                        } catch { return "A"; }
                      })()}
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {(() => {
                        try {
                          const d = JSON.parse(atob(adminToken.split(".")[1]));
                          return d.firstName ? `${d.firstName} ${d.lastName || ""}`.trim() : (d.name || d.email || "Admin");
                        } catch { return "Admin"; }
                      })()}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      localStorage.removeItem("adminToken");
                      history.push("/admin/login");
                    }}
                    className="!text-white py-2 px-5 border border-solid !bg-second !border-second capitalize rounded-full !transition-all !duration-300 hover:!bg-main hover:!border-main"
                  >
                    Logout
                  </button>
                </div>
              ) : location.pathname !== "/admin/login" ? (
                <>
                  <Link to="/how-it-works" className="max-sm:hidden">
                    How it Works
                  </Link>
                  <Link to="/help" className="ml-6 mr-8 max-sm:hidden">
                    Help
                  </Link>
                  <Link
                    to="/login"
                    className="text-second py-3 px-8 border border-solid !border-second bg-transparent capitalize rounded-full transition-all duration-300 hover:!bg-second hover:text-white"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="!text-white py-3 px-8 ml-4 border border-solid !bg-second !border-second capitalize rounded-full !transition-all !duration-300 hover:!bg-main hover:!border-main"
                  >
                    Join Now
                  </Link>
                </>
              ) : null}
            </div>
          )}
          {showState && !adminToken && location.pathname !== "/admin/login" && (
            <div className="flex items-center justify-center mt-6 sm:hidden">
              <Link to="/how-it-works" className="mx-4">
                How it Works
              </Link>
              <span className="text-2xl">/</span>
              <Link to="/help" className="mx-4">
                Help
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  favourite: state.favourite,
  notification: state.notification,
});

export default connect(mapStateToProps, {
  setCurrentUser,
  setCurrentNotifications,
  setCurrentFavorites,
})(Header);
